import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Loader from './Loader';
import { useAuth } from '../contexts/AuthContext';
import fetchJson from '../hooks/fetchJson';

const MoveModal = ({ show, onClose, onSubmitted }) => {
  const { user, token } = useAuth();

  const headers = useMemo(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    if (user?.ownerId) h['x-owner-id'] = String(user.ownerId);
    if (user?.staffId) h['x-staff-id'] = String(user.staffId);
    return h;
  }, [user, token]);

  const [stores, setStores] = useState([]);
  const [fromStoreId, setFromStoreId] = useState('');
  const [toStoreId, setToStoreId] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [batches, setBatches] = useState([]);
  const [quantities, setQuantities] = useState({}); // { [batchId]: number }

  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setFromStoreId('');
    setToStoreId('');
    setProductOptions([]);
    setSelectedProduct(null);
    setBatches([]);
    setQuantities({});
    loadStores();
  }, [show]);

  const loadStores = async () => {
    setLoadingStores(true);
    try {
      const { data, error } = await fetchJson('/api/stores', { headers });
      if (error) throw new Error(error);
      setStores(data?.items || []);
    } catch (e) {
      setStores([]);
      toast.error(e.message || 'Omborlarni yuklashda xatolik');
    } finally {
      setLoadingStores(false);
    }
  };

  const loadProductsForStore = async (storeId) => {
    if (!storeId) {
      setProductOptions([]);
      return;
    }
    setLoadingProducts(true);
    try {
      const { data, error } = await fetchJson(`/api/move-store/stores/${storeId}/products-with-stock`, { headers });
      if (error) throw new Error(error);
      const items = data?.items || [];
      setProductOptions(items.map(p => ({ value: p.id, label: p.name, data: p })));
    } catch (e) {
      setProductOptions([]);
      toast.error(e.message || 'Mahsulotlarni yuklashda xatolik');
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadBatches = async (storeId, productId) => {
    if (!storeId || !productId) {
      setBatches([]);
      return;
    }
    setLoadingBatches(true);
    try {
      const { data, error } = await fetchJson(`/api/move-store/stores/${storeId}/products/${productId}/batches`, { headers });
      if (error) throw new Error(error);
      setBatches(data?.items || []);
      setQuantities({});
    } catch (e) {
      setBatches([]);
      setQuantities({});
      toast.error(e.message || 'Partiyalarni yuklashda xatolik');
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleFromStoreChange = (e) => {
    const id = e.target.value;
    setFromStoreId(id);
    setSelectedProduct(null);
    setProductOptions([]);
    setBatches([]);
    setQuantities({});
    if (id) loadProductsForStore(id);
  };

  const handleProductSelect = (opt) => {
    setSelectedProduct(opt);
    setBatches([]);
    setQuantities({});
    if (opt?.value && fromStoreId) loadBatches(fromStoreId, opt.value);
  };

  const handleQtyChange = (batch, raw) => {
    let val = raw;
    if (val === '' || val === null || typeof val === 'undefined') {
      setQuantities(q => ({ ...q, [batch.id]: '' }));
      return;
    }
    let num = Number(val);
    if (Number.isNaN(num)) return;
    const isSpecific = String(batch.costing_method || '').toUpperCase() === 'SPECIFIC';
    if (isSpecific) {
      num = Math.floor(num);
    } else {
      num = Math.round(num * 1000) / 1000;
    }
    if (num < 0) num = 0;
    if (num > Number(batch.quantity_remaining || 0)) num = Number(batch.quantity_remaining || 0);
    setQuantities(q => ({ ...q, [batch.id]: num }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!fromStoreId) return toast.error('Qayerdan (ombor) tanlanmagan');
    if (!toStoreId) return toast.error('Qayerga (ombor) tanlanmagan');
    if (String(fromStoreId) === String(toStoreId)) return toast.error('Bir xil ombor tanlanmasin');
    if (!selectedProduct?.value) return toast.error('Mahsulot tanlanmagan');

    const lines = batches
      .map(b => ({ batch: b, qty: Number(quantities[b.id] || 0) }))
      .filter(x => x.qty > 0)
      .map(x => ({ purchaseBatchId: x.batch.id, quantity: x.qty }));
    if (lines.length === 0) return toast.error('Koʻchirish uchun miqdor kiritilmagan');

    setSubmitting(true);
    try {
      const payload = {
        fromStoreId: Number(fromStoreId),
        toStoreId: Number(toStoreId),
        productId: Number(selectedProduct.value),
        items: lines,
      };
      const { error } = await fetchJson('/api/move-store/transfers', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (error) throw new Error(error);
      toast.success('Koʻchirish muvaffaqiyatli yuborildi');
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      toast.error(e.message || 'Koʻchirishni yuborishda xatolik');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Mahsulotlarni ko'chirish</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Top 3 elements */}
        <Form onSubmit={handleSubmit}>
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-4">
              <Form.Label>Qayerdan (ombor)</Form.Label>
              <Form.Select value={fromStoreId} onChange={handleFromStoreChange} disabled={loadingStores} className="form-select">
                <option value="">Omborni tanlang</option>
                {stores.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-12 col-md-4">
              <Form.Label>Mahsulot</Form.Label>
              {loadingProducts ? (
                <div className="border rounded p-2"><Loader /></div>
              ) : (
                <Select
                  isDisabled={!fromStoreId}
                  options={productOptions}
                  value={selectedProduct}
                  onChange={handleProductSelect}
                  placeholder="Mahsulotni qidiring..."
                />
              )}
            </div>
            <div className="col-12 col-md-4">
              <Form.Label>Qayerga (ombor)</Form.Label>
              <Form.Select value={toStoreId} onChange={(e) => setToStoreId(e.target.value)} className="form-select">
                <option value="">Omborni tanlang</option>
                {stores
                  .filter(s => String(s.id) !== String(fromStoreId))
                  .map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Form.Select>
            </div>
          </div>

          {/* Available batches section */}
          <div className="mb-2 fw-semibold">Mavjud partiyalar</div>
          <div className="border rounded" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {loadingBatches ? (
              <div className="p-3"><Loader /></div>
            ) : batches.length === 0 ? (
              <div className="p-3 text-muted">Partiyalar topilmadi</div>
            ) : (
              <Table striped bordered hover responsive className="mb-0">
                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Mahsulot</th>
                    <th>Yetkazib beruvchi</th>
                    <th>Sotib olingan</th>
                    <th>Qolgan</th>
                    <th>Ko'chirish miqdori</th>
                    <th>Tan narxi</th>
                    <th>Valyuta</th>
                    <th>Birlamchi narx</th>
                    <th>Sotuv narxi</th>
                    <th>Shtrixkod</th>
                    <th>Ombor</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map(b => {
                    const isSpecific = String(b.costing_method || '').toUpperCase() === 'SPECIFIC';
                    const step = isSpecific ? 1 : 0.001;
                    return (
                      <tr key={b.id}>
                        <td>{b.product_name || '-'}</td>
                        <td>{b.supplier_name || '-'}</td>
                        <td>{Number(b.quantity_purchased || 0)}</td>
                        <td>{Number(b.quantity_remaining || 0)}</td>
                        <td style={{ minWidth: 150 }}>
                          <Form.Control
                            type="number"
                            min={0}
                            max={Number(b.quantity_remaining || 0)}
                            step={step}
                            value={quantities[b.id] ?? ''}
                            onChange={(e) => handleQtyChange(b, e.target.value)}
                            placeholder={isSpecific ? 'butun son' : '0.000'}
                          />
                        </td>
                        <td>{Number(b.costperunit || 0)}</td>
                        <td>{String(b.currency || '').toUpperCase()}</td>
                        <td>{Number(b.first_price || 0)}</td>
                        <td>{Number(b.sellingprice || 0)}</td>
                        <td>{b.barcode || '-'}</td>
                        <td>{b.store_name || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>Bekor qilish</Button>
        <Button variant="success" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Yuborilmoqda...' : 'Ko\'chirish'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MoveModal;
